
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function SuccessStories() {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Success Stories - Negotrix | Measurable Commercial Outcomes';
    setTimeout(() => setIsVisible(true), 100);
  }, []);

  const stories = [
    {
      title: 'Series C SaaS Company',
      industry: 'Technology',
      type: 'Cloud & SaaS',
      desc: 'Identified zombie AWS instances, right-sized databases, and renegotiated Salesforce and HubSpot contracts. Full implementation completed in 76 days.',
      categories: ['Cloud Infrastructure', 'SaaS Contracts'],
    },
    {
      title: 'Global E-Commerce Brand',
      industry: 'E-Commerce',
      type: 'Vendor & Marketing',
      desc: 'Audited agency fees, consolidated logistics contracts, and improved marketing tech stack. Delivered strong commercial outcomes within the first 30 days.',
      categories: ['Vendor Contracts', 'Marketing Spend'],
    },
    {
      title: 'Financial Services Firm',
      industry: 'Finance',
      type: 'SaaS & Cloud',
      desc: 'Renegotiated software licenses, improved cloud infrastructure, and identified 34 duplicate subscriptions across departments.',
      categories: ['SaaS & Software', 'Cloud'],
    },
    {
      title: 'Healthcare Tech Startup',
      industry: 'Healthcare',
      type: 'SaaS & Cloud',
      desc: 'Consolidated SaaS tools from 87 to 52, improved AWS usage by 40%, and renegotiated 12 vendor contracts.',
      categories: ['SaaS Consolidation', 'Cloud', 'Vendor Contracts'],
    },
  ];

  return (
    <div className={`min-h-screen bg-cipher-black text-white transition-opacity duration-700 ${isVisible ? 'opacity-100' : 'opacity-0'}`}>
      <Header />

      {/* Hero */}
      <section className="pt-40 pb-24 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-6">Client Impact</h2>
          <h1 className="text-[9vw] leading-[0.9] font-black tracking-tighter mb-12">
            <span className="text-cipher-hypergreen">Strong commercial</span><br />
            <span className="text-white">outcomes.</span>
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            Real results from real businesses. See how companies like yours have achieved stronger commercial outcomes across all spending categories.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 bg-cipher-black">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <h2 className="text-sm tracking-widest uppercase text-cipher-hypergreen font-bold mb-16">Case Studies</h2>
          <div className="border-t border-gray-800">
            {stories.map((story, idx) => (
              <div key={idx} className="group border-b border-gray-800 py-16 hover:bg-cipher-dark transition-colors px-6 -mx-6">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                  <div className="lg:col-span-3">
                    <div className="text-5xl lg:text-7xl font-black text-cipher-hypergreen mb-3">{story.type}</div>
                     <div className="text-sm tracking-widest uppercase text-gray-500 font-bold">Engagement</div>
                  </div>
                  <div className="lg:col-span-5">
                    <div className="text-2xl lg:text-3xl font-black text-white mb-1">{story.title}</div>
                    <div className="text-cipher-hypergreen text-sm font-bold tracking-widest uppercase mb-6">{story.industry}</div>
                    <p className="text-gray-400 text-lg leading-relaxed">{story.desc}</p>
                  </div>
                  <div className="lg:col-span-4">
                    <div className="text-xs tracking-widest uppercase text-gray-600 font-bold mb-4">Categories</div>
                    <div className="flex flex-wrap gap-2">
                      {story.categories.map((c, i) => (
                        <span key={i} className="px-3 py-1.5 bg-cipher-dark border border-gray-800 text-gray-400 text-sm rounded-full">{c}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-24 bg-cipher-dark border-t border-gray-900">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12">
          <blockquote className="max-w-5xl">
            <p className="text-3xl lg:text-5xl font-black text-white leading-tight mb-12">
              "Negotrix found commercial outcomes we didn't know existed. Their team was professional, thorough, and delivered results in under 90 days."
            </p>
            <footer>
              <div className="text-cipher-hypergreen font-bold text-lg">CTO, Series C SaaS Company</div>
            </footer>
          </blockquote>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 lg:py-48 bg-cipher-black border-t border-gray-800">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 text-center">
          <h2 className="text-[8vw] leading-[0.9] font-black tracking-tighter text-white mb-12">
             Start your commercial<br /><span className="text-cipher-hypergreen">review today.</span>
          </h2>
          <button
            onClick={() => navigate('/contact')}
            className="inline-flex items-center justify-center px-10 py-5 text-lg tracking-wide font-bold uppercase rounded-full text-cipher-black bg-cipher-hypergreen hover:bg-white transition-all duration-300"
          >
             Start Your Commercial Review
          </button>
        </div>
      </section>

      <Footer />
    </div>
  );
}
